import { useEffect, useRef } from 'react';
import { RtkScreenshareView } from '@cloudflare/realtimekit-react-ui';
import { useMeetingProvider } from 'contexts/MeetingProviderContext';
import useMeeting from 'hooks/useMeeting';

interface LiveKitScreenShareViewProps {
  screenShareTrack: MediaStreamTrack;
}

/**
 * LiveKit Screen Share View
 * Renders screen share using native HTML5 video element
 */
const LiveKitScreenShareView: React.FC<LiveKitScreenShareViewProps> = ({ screenShareTrack }) => {
  const videoRef = useRef<HTMLVideoElement>(null);
  const trackIdRef = useRef<string | null>(null);

  useEffect(() => {
    const currentTrackId = screenShareTrack?.id || null;

    if (currentTrackId !== trackIdRef.current) {
      trackIdRef.current = currentTrackId;

      if (screenShareTrack && videoRef.current) {
        const stream = new MediaStream([screenShareTrack]);
        videoRef.current.srcObject = stream;
        videoRef.current.play().catch((err) => {
          console.error('Error playing screen share:', err);
        });
      } else if (videoRef.current) {
        videoRef.current.srcObject = null;
      }
    }
  }, [screenShareTrack]);

  useEffect(() => {
    return () => {
      if (videoRef.current) {
        videoRef.current.srcObject = null;
      }
    };
  }, []);

  return (
    <video
      ref={videoRef}
      autoPlay
      playsInline
      muted
      style={{
        width: '100%',
        height: '100%',
        objectFit: 'contain',
        backgroundColor: '#000',
      }}
    />
  );
};

/**
 * Provider-agnostic ScreenShare component
 * Routes to the correct implementation based on the meeting provider
 */
export default function ScreenShare() {
  const { provider } = useMeetingProvider();
  const { screenShareHost, meeting } = useMeeting();

  if (!screenShareHost) {
    return null;
  }

  // LiveKit: Use native video element with screenShareTrack
  if (provider === 'livekit') {
    const screenShareTrack = screenShareHost.screenShareTrack;

    // If screen share is enabled but track is not yet available, show loading state
    if (!screenShareTrack) {
      return (
        <div className='w-full h-full flex items-center justify-center bg-black'>
          <div className='text-white text-sm'>Loading screen share...</div>
        </div>
      );
    }

    return (
      <div className='w-full h-full'>
        <LiveKitScreenShareView screenShareTrack={screenShareTrack} />
      </div>
    );
  }

  // Dyte: Use DyteScreenshareView component with raw participant
  return (
    <div className='w-full h-full'>
      <RtkScreenshareView
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        participant={(screenShareHost._raw || screenShareHost) as any}
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        meeting={meeting as any}
      />
    </div>
  );
}
